import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LanguageSwitcher from 'shared/components/LanguageSwitcher';
import { useOnboarding } from 'features/onboarding';

import { pageVariants } from '../animations';
import ChannelStep from '../components/ChannelStep';
import CredentialsStep from '../components/CredentialsStep';
import FloatingElements from '../components/FloatingElements';
import LoginCard, { LoginStep } from '../components/LoginCard';
import LoginLeftPanel from '../components/LoginLeftPanel';
import LoginLogo from '../components/LoginLogo';
import OtpStep from '../components/OtpStep';
import { usePartnerAuth } from '../context/PartnerAuthContext';
import { partnerAuthService } from '../services/partnerAuth.service';
import type { OtpMethod } from '../types';

const STEP_ORDER: LoginStep[] = ['credentials', 'channel', 'otp'];

const PartnerLoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setPartner } = usePartnerAuth();
  const { reset: resetOnboarding } = useOnboarding();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [direction, setDirection] = useState(1);
  const [phone, setPhone] = useState('');

  const [ticketId, setTicketId] = useState<string | null>(null);
  const [availableMethods, setAvailableMethods] = useState<OtpMethod[]>([
    'sms',
  ]);
  const [maskedPhone, setMaskedPhone] = useState<string | undefined>();
  const [maskedEmail, setMaskedEmail] = useState<string | undefined>();
  const [selectedChannel, setSelectedChannel] = useState<OtpMethod>('sms');

  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(180);
  const [devOtp, setDevOtp] = useState<string | null>(null);

  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingCreds, setIsVerifyingCreds] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSuccess, setOtpSuccess] = useState(false);

  const goTo = (next: LoginStep) => {
    const nextIdx = STEP_ORDER.indexOf(next);
    const currentIdx = STEP_ORDER.indexOf(step);
    setDirection(nextIdx >= currentIdx ? 1 : -1);
    setStep(next);
  };

  const handleVerifyCredentials = async (nextPhone: string) => {
    setPhone(nextPhone);
    setCredentialsError(null);
    setIsVerifyingCreds(true);
    try {
      const result = await partnerAuthService.verifyCredentials({
        phone: nextPhone,
      });
      setTicketId(result.ticketId);
      setAvailableMethods(result.availableMethods);
      setMaskedPhone(result.maskedPhone);
      setMaskedEmail(result.maskedEmail);
      setSelectedChannel(
        result.availableMethods.includes('sms')
          ? 'sms'
          : (result.availableMethods[0] ?? 'sms'),
      );
      setChannelError(null);
      goTo('channel');
    } catch (err) {
      setCredentialsError(
        extractError(err, t('partnerAuth.errors.credentials')),
      );
    } finally {
      setIsVerifyingCreds(false);
    }
  };

  const handleSendOtp = async () => {
    if (!ticketId || isSendingOtp) return;
    setChannelError(null);
    setIsSendingOtp(true);
    try {
      const result = await partnerAuthService.sendOtp(
        ticketId,
        selectedChannel,
      );
      setChallengeId(result.challengeId);
      setSentTo(result.sentTo);
      setOtpExpiresIn(result.otpExpiresIn);
      setDevOtp(result.devOtp ?? null);
      setOtpError(null);
      setOtpSuccess(false);
      goTo('otp');
    } catch (err) {
      setChannelError(extractError(err, t('partnerAuth.errors.sendOtp')));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!challengeId) return;
    try {
      const result = await partnerAuthService.resendOtp(challengeId);
      setChallengeId(result.challengeId);
      setSentTo(result.sentTo);
      setOtpExpiresIn(result.otpExpiresIn);
      setDevOtp(result.devOtp ?? null);
      setOtpError(null);
    } catch (err) {
      setOtpError(extractError(err, t('partnerAuth.errors.resend')));
    }
  };

  const handleStartHere = () => {
    resetOnboarding();
    navigate('/');
  };

  const handleVerifyOtp = async (code: string) => {
    if (!challengeId || isVerifyingOtp || otpSuccess) return;
    setIsVerifyingOtp(true);
    setOtpError(null);
    try {
      const { partner } = await partnerAuthService.verifyOtp(challengeId, code);
      setPartner(partner);
      setOtpSuccess(true);
      window.setTimeout(
        () => navigate('/portal/status', { replace: true }),
        700,
      );
    } catch (err) {
      setOtpError(extractError(err, t('partnerAuth.errors.otp')));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      className="min-h-screen flex bg-[#fbf7f6] relative"
    >
      <LoginLeftPanel />
      <div className="relative w-full lg:w-1/2 flex items-center justify-center p-8 bg-white overflow-hidden">
        <div className="absolute top-5 z-20 ltr:right-5 rtl:left-5">
          <LanguageSwitcher variant="header" />
        </div>
        <FloatingElements />
        <div className="relative w-full max-w-md">
          <LoginLogo />

          <LoginCard step={step} direction={direction}>
            {step === 'credentials' && (
              <CredentialsStep
                initialPhone={phone}
                isSubmitting={isVerifyingCreds}
                errorMessage={credentialsError}
                onSubmit={handleVerifyCredentials}
              />
            )}
            {step === 'channel' && (
              <ChannelStep
                selected={selectedChannel}
                availableMethods={availableMethods}
                maskedPhone={maskedPhone}
                maskedEmail={maskedEmail}
                isSubmitting={isSendingOtp}
                errorMessage={channelError}
                onSelect={setSelectedChannel}
                onBack={() => goTo('credentials')}
                onContinue={handleSendOtp}
              />
            )}
            {step === 'otp' && (
              <OtpStep
                sentTo={sentTo}
                expiresIn={otpExpiresIn}
                isVerifying={isVerifyingOtp}
                isSuccess={otpSuccess}
                errorMessage={otpError}
                devOtp={devOtp}
                onBack={() => goTo('channel')}
                onSubmit={handleVerifyOtp}
                onResend={handleResendOtp}
              />
            )}
          </LoginCard>

          <p className="text-center text-xs text-gray-500 mt-5">
            {t('partnerAuth.footer.notRegistered')}{' '}
            <button
              type="button"
              onClick={handleStartHere}
              className="font-semibold text-haat-red-600 hover:text-haat-red-700"
            >
              {t('partnerAuth.footer.startHere')}
            </button>
          </p>

          <footer className="text-center mt-8 text-sm text-gray-400">
            <p>
              {t('partnerAuth.footer.copyright', {
                year: new Date().getFullYear(),
              })}
            </p>
          </footer>
        </div>
      </div>
    </motion.div>
  );
};

const extractError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ message?: string | string[] }>;
    const msg = ax.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(' • ');
    if (typeof msg === 'string') return msg;
  }
  return fallback;
};

export default PartnerLoginPage;
